<template>
    <div class="entity-page">
        <header class="entity-hero page-gutter">
            <router-link :to="isPartner ? '/Partners' : '/Teams'" class="entity-hero__back">
                <v-icon small color="#1b2f5a">mdi-arrow-left</v-icon>
                {{ isPartner ? 'Επιχειρήσεις - Εκπτώσεις' : 'Σωματεία' }}
            </router-link>

            <div v-if="notFound" class="entity-missing">
                <p>{{ isPartner ? 'Η επιχείρηση δεν βρέθηκε.' : 'Το σωματείο δεν βρέθηκε.' }}</p>
            </div>

            <div v-else class="entity-hero__main">
                <div class="entity-hero__logo">
                    <img
                        v-if="imageSrc && !imageFailed"
                        :src="imageSrc"
                        :alt="data.title"
                        @error="imageFailed = true"
                    >
                    <img v-else class="entity-hero__placeholder" :src="require('@/assets/LOGOPARON 2023.svg')" alt="">
                </div>
                <div class="entity-hero__info">
                    <v-skeleton-loader v-if="loading" type="heading, text" max-width="520" />
                    <template v-else>
                        <span v-if="category" class="entity-hero__chip">{{ category }}</span>
                        <h1 class="entity-hero__title">{{ data.title }}</h1>
                        <div v-if="data.type" class="entity-hero__type">{{ data.type }}</div>
                        <div v-if="data.address" class="entity-hero__address">
                            <v-icon small color="#ad282d">mdi-map-marker-outline</v-icon>
                            {{ data.address }}
                        </div>
                    </template>
                </div>
            </div>
        </header>

        <div v-if="!notFound && !loading" class="entity-body page-gutter">
            <v-row>
                <v-col cols="12" :md="hasSidebar ? 8 : 12">
                    <section v-if="isPartner" class="entity-section">
                        <h2 class="entity-section__title">
                            <v-icon color="#ad282d" class="mr-2">mdi-ticket-percent-outline</v-icon>
                            Εκπτώσεις για τα μέλη
                        </h2>
                        <div v-if="visibleOffers.length" class="offer-list">
                            <div v-for="offer in visibleOffers" :key="offer.id" class="offer">
                                <div class="offer__text">{{ offer.description }}</div>
                                <div v-if="offer.secret || expiry(offer)" class="offer__meta">
                                    <span v-if="offer.secret" class="offer__badge">Μόνο για κατόχους κάρτας</span>
                                    <span v-if="expiry(offer)">Ισχύει έως {{ expiry(offer) }}</span>
                                </div>
                            </div>
                        </div>
                        <p v-else class="entity-muted">
                            Δείξτε την PARON CARD στο κατάστημα για να ενημερωθείτε για τις εκπτώσεις.
                        </p>
                        <router-link v-if="hiddenSecretOffers" to="/Login" class="offer-login">
                            <v-icon small color="#1b2f5a">mdi-lock-outline</v-icon>
                            Συνδεθείτε με την κάρτα σας για να δείτε {{ hiddenSecretOffers === 1 ? 'ακόμη μία προσφορά' : `ακόμη ${hiddenSecretOffers} προσφορές` }}
                        </router-link>
                    </section>

                    <section v-if="data.description" class="entity-section">
                        <h2 class="entity-section__title">{{ isPartner ? 'Σχετικά με την επιχείρηση' : 'Σχετικά με το σωματείο' }}</h2>
                        <div class="entity-description" v-html="data.description"></div>
                    </section>
                </v-col>

                <v-col v-if="hasSidebar" cols="12" md="4">
                    <aside class="entity-section entity-location">
                        <h2 class="entity-section__title">Τοποθεσία</h2>
                        <gmap-map
                            v-if="hasLocation"
                            :center="center"
                            :zoom="16"
                            :options="options"
                            class="entity-location__map"
                        >
                            <gmap-marker :position="center" />
                        </gmap-map>
                        <div v-if="data.address" class="entity-location__address">
                            <v-icon small color="#ad282d">mdi-map-marker-outline</v-icon>
                            {{ data.address }}
                        </div>
                        <v-btn
                            v-if="addressUrl"
                            block
                            depressed
                            color="#1b2f5a"
                            dark
                            class="mt-4"
                            :href="addressUrl"
                            target="_blank"
                            rel="noopener"
                        >
                            <v-icon left small>mdi-directions</v-icon>Οδηγίες
                        </v-btn>
                    </aside>
                </v-col>
            </v-row>
        </div>
    </div>
</template>


<script>
import '@/plugins/google-maps'
import restApi from "@/rest-api"
import { formatAthensDate } from '@/utils/date'

export default {
    data() {
        return {
            data: {},
            loading: true,
            notFound: false,
            imageFailed: false,
            center: { lat: 0, lng: 0 },
            options: {
                zoomControl: true,
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: true,
                disableDefaultUI: false
            },
            hasLocation: false,
            addressUrl: ''
        }
    },
    computed: {
        isPartner() {
            return this.$route.path !== '/Team'
        },
        // members-only offers are shown to visitors logged in with their card
        isCardHolder() {
            return !!this.$store.getters.getCard.card
        },
        imageSrc() {
            return restApi.imageUrl(this.data.image)
        },
        category() {
            const category = this.data.category
            return category && typeof category === 'object' ? category.title : category
        },
        activeOffers() {
            return (this.data.offers || []).filter(offer => offer && offer.active && offer.description)
        },
        visibleOffers() {
            return this.activeOffers.filter(offer => !offer.secret || this.isCardHolder)
        },
        hiddenSecretOffers() {
            return this.isCardHolder ? 0 : this.activeOffers.filter(offer => offer.secret).length
        },
        hasSidebar() {
            return this.hasLocation || !!this.data.address
        }
    },
    watch: {
        // moving from one partner/team to another only changes ?id=
        '$route.query.id'() {
            this.fetchData()
        }
    },
    created() {
        this.fetchData();
    },
    methods: {
        fetchData() {
            const id = this.$route.query.id
            this.loading = true
            this.notFound = false
            this.imageFailed = false
            const request = this.isPartner ? restApi.get(`/partner/${id}`) : restApi.get(`/team/${id}`)
            request
                .then(response => {
                    const data = response && (this.isPartner ? response.partner : response.team)
                    if (!data) {
                        this.notFound = true
                        return
                    }
                    this.setData(data)
                })
                .catch(() => {
                    this.notFound = true
                })
                .finally(() => {
                    this.loading = false
                });
        },
        setData(data) {
            this.data = data || {}
            document.title = `${this.data.title} | PARON CARD`
            const coordinates = (this.data.coordinates || '').split(',').map(value => parseFloat(value))
            this.hasLocation = coordinates.length === 2 && coordinates.every(value => !Number.isNaN(value))
            if (this.hasLocation) {
                this.center = { lat: coordinates[0], lng: coordinates[1] }
            }
            const query = [this.data.address, this.hasLocation ? this.data.coordinates : null].filter(Boolean).join(' ')
            this.addressUrl = query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : ''
        },
        // some offers carry placeholder dates (e.g. year 0031); only show real, future ones
        expiry(offer) {
            const date = offer.expirationDate ? new Date(offer.expirationDate) : null
            if (!date || Number.isNaN(date.getTime()) || date.getFullYear() < 2000 || date < new Date()) {
                return ''
            }
            return formatAthensDate(date)
        }
    }
}
</script>

<style lang="scss" scoped>
.entity-page {
    color: #1b2f5a;
    padding-bottom: 56px;
}

.entity-hero {
    padding-top: 28px;
    padding-bottom: 36px;
    background: linear-gradient(180deg, #f4f6fb 0%, #ffffff 100%);
    border-bottom: 1px solid rgba(27, 47, 90, 0.08);
}

.entity-hero__back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #1b2f5a !important;
    text-decoration: none;
    font-size: 15px;
    font-weight: 600;
    opacity: 0.8;
}

.entity-hero__back:hover {
    opacity: 1;
}

.entity-hero__main {
    display: flex;
    align-items: center;
    gap: 32px;
    margin-top: 20px;
}

.entity-hero__logo {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 220px;
    height: 220px;
    padding: 20px;
    background: #fff;
    border: 1px solid rgba(27, 47, 90, 0.1);
    border-radius: 20px;
    box-shadow: 0 10px 28px rgba(27, 47, 90, 0.1);
}

.entity-hero__logo img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.entity-hero__placeholder {
    width: 96px;
    opacity: 0.35;
}

.entity-hero__info {
    flex: 1;
    min-width: 0;
}

.entity-hero__chip {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 999px;
    background: #1b2f5a;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
}

.entity-hero__title {
    margin: 12px 0 6px;
    font-size: clamp(26px, 3.4vw, 38px);
    font-weight: 700;
    line-height: 1.2;
}

.entity-hero__type {
    font-size: 17px;
    color: rgba(27, 47, 90, 0.75);
}

.entity-hero__address {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 14px;
    font-size: 15px;
    color: rgba(27, 47, 90, 0.85);
}

.entity-body {
    margin-top: 28px;
}

.entity-section {
    background: #fff;
    border: 1px solid rgba(27, 47, 90, 0.1);
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 1px 2px rgba(27, 47, 90, 0.05);
}

.entity-section__title {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    font-size: 20px;
    font-weight: 700;
}

.offer-list {
    display: grid;
    gap: 12px;
}

.offer {
    padding: 16px 18px;
    border-left: 4px solid #ad282d;
    border-radius: 10px;
    background: rgba(173, 40, 45, 0.06);
}

.offer__text {
    font-size: 17px;
    font-weight: 600;
    color: #8f1f23;
    line-height: 1.4;
}

.offer__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 6px;
    font-size: 13px;
    color: rgba(27, 47, 90, 0.7);
}

.offer__badge {
    padding: 1px 8px;
    border-radius: 999px;
    background: #1b2f5a;
    color: #fff;
    font-weight: 600;
}

.offer-login {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 14px;
    color: #1b2f5a !important;
    font-size: 14px;
    font-weight: 600;
}

.entity-muted {
    margin: 0;
    color: rgba(27, 47, 90, 0.75);
}

.entity-description {
    font-size: 16px;
    line-height: 1.7;
    overflow-wrap: anywhere;
}

/* content comes from the CMS editor */
.entity-description ::v-deep img {
    max-width: 100% !important;
    height: auto;
}

.entity-description ::v-deep p:last-child {
    margin-bottom: 0;
}

.entity-description ::v-deep a {
    color: #ad282d;
}

.entity-location {
    position: sticky;
    top: 24px;
}

.entity-location__map {
    width: 100%;
    height: 240px;
    border-radius: 12px;
    overflow: hidden;
}

.entity-location__address {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 14px;
    font-size: 15px;
    line-height: 1.5;
}

.entity-missing {
    padding: 48px 0;
    font-size: 18px;
}

@media (max-width: 700px) {
    .entity-hero__main {
        flex-direction: column;
        align-items: flex-start;
        gap: 20px;
    }

    .entity-hero__logo {
        width: 160px;
        height: 160px;
    }

    .entity-section {
        padding: 18px;
    }
}
</style>
